import {
    ArrowCircleDown16Regular,
    ArrowCircleUp16Filled,
    ArrowCircleUp16Regular,
    EqualCircleRegular,
} from '@fluentui/react-icons';
import classNames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import {
    copilotInboxPriorityIconForMLStyles,
    copilotInboxPriorityIconForSettingsStyles,
    copilotInboxPriorityIconForRPStyles,
    copilotInboxPriorityIconForRPStylesCozyAndCompact,
    copilotInboxHighPriorityIconColor,
    copilotInboxLowPriorityIconColor,
    copilotInboxNormalPriorityColor,
    copilotInboxPriorityHighContrastColor,
    iconContainer,
    copilotHighPriorityLabel,
    copilotLowPriorityLabel,
    iconAndTextContainer,
} from './CopilotInboxPriorityIcon.scss';
import getCopilotPrioritizeSettings from 'owa-mail-copilot-settings/lib/selectors/getCopilotPrioritizeSettings';
import type { MailboxInfo } from 'owa-client-types';
import {
    highPriorityIconTooltip,
    lowPriorityIconTooltip,
    highPriorityLabel,
    lowPriorityLabel,
} from './CopilotInboxPriorityIcon.locstring.json';
import loc from 'owa-localize';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { isCopilotHighPriority, isCopilotLowPriority } from '../utils/priorityUtils';
import type { FunctionComponent } from 'react';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isPrioritizeOnDeliveryOn, isPrioritizeOnDemandOn } from 'owa-copilot-settings-utils';

interface CopilotInboxPriorityIconProps {
    copilotInboxScore?: number;
    priorityOverride?: 'high' | 'low' | 'normal';
    scenario:
        | 'ML'
        | 'Settings'
        | 'RP'
        | 'PrioritySort'
        | 'OnboardedCallout'
        | 'Notifications'
        | 'PrioritizeBar';
    mailboxInfo?: MailboxInfo;
    isUnread?: boolean;
    isSortedContentPrioritySet?: boolean;
    isPriorityView?: boolean;
    copilotPrioritizeOnDeliveryOn?: boolean;
}

const defaultExport: FunctionComponent<CopilotInboxPriorityIconProps> = observer(
    function CopilotInboxPriorityIcon(props: CopilotInboxPriorityIconProps) {
        const {
            copilotInboxScore = 0,
            scenario,
            priorityOverride,
            mailboxInfo,
            isUnread = true,
            isSortedContentPrioritySet = false,
            isPriorityView = false,
            copilotPrioritizeOnDeliveryOn,
        } = props;

        // Fast path: if there's no priority override and the score indicates no
        // priority, skip the expensive isPrioritizeOn checks entirely.
        // isCopilotHighPriority/isCopilotLowPriority are cheap numeric comparisons.
        const hasScoreBasedPriority =
            isCopilotHighPriority(copilotInboxScore) || isCopilotLowPriority(copilotInboxScore);
        if (!priorityOverride && !hasScoreBasedPriority) {
            return null;
        }

        const prioritizeOnDeliveryOn =
            copilotPrioritizeOnDeliveryOn ??
            (mailboxInfo ? isPrioritizeOnDeliveryOn(mailboxInfo) : false);
        const prioritizeOnDemandOn =
            mailboxInfo && !prioritizeOnDeliveryOn ? isPrioritizeOnDemandOn(mailboxInfo) : false;
        const prioritizeOn = mailboxInfo ? prioritizeOnDeliveryOn || prioritizeOnDemandOn : null;
        const applyLowPriorityLabel = mailboxInfo
            ? getCopilotPrioritizeSettings(mailboxInfo).applyLowPriorityLabel
            : null;

        const isHighPriority =
            priorityOverride === 'high' ||
            (prioritizeOn && isCopilotHighPriority(copilotInboxScore));

        const isLowPriority =
            priorityOverride === 'low' ||
            (prioritizeOn &&
                (applyLowPriorityLabel ||
                    scenario === 'PrioritySort' ||
                    scenario == 'Notifications') &&
                isCopilotLowPriority(copilotInboxScore));

        const isNormalPriority = priorityOverride === 'normal';

        let scenarioStyles = null;
        switch (scenario) {
            case 'ML':
            case 'PrioritySort':
            case 'OnboardedCallout':
            case 'Notifications':
                scenarioStyles = copilotInboxPriorityIconForMLStyles;
                break;
            case 'Settings':
                scenarioStyles = copilotInboxPriorityIconForSettingsStyles;
                break;
            case 'RP':
                scenarioStyles = getDensityModeCssClass(
                    copilotInboxPriorityIconForRPStyles,
                    copilotInboxPriorityIconForRPStylesCozyAndCompact,
                    copilotInboxPriorityIconForRPStylesCozyAndCompact
                );
                break;
        }

        const iconStyles = classNames(
            isHighPriority && copilotInboxHighPriorityIconColor,
            isLowPriority && copilotInboxLowPriorityIconColor,
            isNormalPriority && copilotInboxNormalPriorityColor,
            copilotInboxPriorityHighContrastColor,
            scenarioStyles
        );

        let icon = null;

        if (isLowPriority) {
            icon = <ArrowCircleDown16Regular className={iconStyles} />;
        } else if (isHighPriority) {
            if (isUnread) {
                icon = <ArrowCircleUp16Filled className={iconStyles} />;
            } else {
                icon = <ArrowCircleUp16Regular className={iconStyles} />;
            }
        } else if (isNormalPriority) {
            icon = <EqualCircleRegular className={iconStyles} />;
        }

        if (!icon) {
            return null;
        }

        const iconTitle = isHighPriority
            ? loc(highPriorityIconTooltip)
            : loc(lowPriorityIconTooltip);

        const shouldShowPriorityLabel =
            scenario === 'Notifications' &&
            isGlobalFeatureEnabled('notif-copilotPrioritize') &&
            !isSortedContentPrioritySet;
        const priotityLabel = isHighPriority ? loc(highPriorityLabel) : loc(lowPriorityLabel);

        if (
            scenario === 'ML' &&
            mailboxInfo &&
            !(prioritizeOnDeliveryOn || (prioritizeOnDemandOn && isPriorityView))
        ) {
            return null;
        }

        return (
            <div
                className={scenario === 'Notifications' ? iconAndTextContainer : iconContainer}
                title={scenario === 'ML' ? iconTitle : undefined}
            >
                {icon}
                {shouldShowPriorityLabel && (
                    <span
                        className={
                            isHighPriority ? copilotHighPriorityLabel : copilotLowPriorityLabel
                        }
                    >
                        {priotityLabel}
                    </span>
                )}
            </div>
        );
    },
    'CopilotInboxPriorityIcon'
);
export default defaultExport;
